import { useRouter } from 'next/router'
import { useEffect, useRef, useState } from 'react'
import { createFetchWebEventCollector, type Currency, EventName, PageName } from 'web-event-collector'
import { getEnv } from '@unni/platform'

import ResponsiveLayout from '@/components/atomic/templates/ResponsiveLayout'
import SearchTemplate, { type OnSearchTemplateEventClickParam } from '@/components/atomic/templates/SearchTemplate'
import TopDownloadScreen from '@/components/atomic/templates/TopDownloadScreen'
import { addCriteoEvent, postAmplitude } from '@/core/lib'
import { getPlaceByPathname } from '@/utils/amplitude'
import { createCommonEvent } from '@/utils/eventCollector'
import { User } from '@/features/member/member.type'
import { AirbridgeEvents, sendAirbridgeEvent } from '@/utils/airbridge'
import { JsonLd } from '@/components'
import { getHospitalProductJsonLdWithContext } from '@/utils/scheme'
import { SEOMeta } from '@/shared/utils'

import type { LegacySearch } from '@/common/models/search'
import type { HospitalInList } from '@/features/hospital/types/hospital.type'
import type { NextPage } from 'next'

interface SearchPageProps extends LegacySearch {
  user: User | null
  keyword: string
  treatmentCategoryName: string
  treatmentCategoryId: number
  token: string
  language: string
  blogs: Post[]
  blogTotalLength: number
  meta: SEOMeta
}

export interface SearchPageEventImpressedEventParam {
  id: number
  hospitalId: number
  cost: number
  currency: string
}

const Search: NextPage<SearchPageProps> = ({
  user,
  keyword,
  treatmentCategoryName,
  treatmentCategoryId,
  treatments,
  treatmentTotalLength,
  serviceOfferItems,
  serviceOfferTotalLength,
  communityDocuments,
  communityDocumentTotalLength,
  hospitals,
  hospitalTotalLength,
  blogs,
  blogTotalLength,
  reviews,
  reviewTotalLength,
  token,
  language,
  totalLength,
}) => {
  const [commonAmplitudeProperties, setCommonAmplitudeProperties] = useState({})
  const router = useRouter()
  const category = router.query.category?.toString()
  const q = router.query.q?.toString()

  const handleClickTreatment = (treatmentId: number) => {
    router.push(`/treatments/${treatmentId}`)
  }

  const handleClickEvent = (event: OnSearchTemplateEventClickParam) => {
    postAmplitude('click_event_in_search', {
      ...commonAmplitudeProperties,
      event_id: event?.id,
      event_title: event?.title,
      hospital_country: event?.hospitalCountry,
      hospital_region: event?.hospitalSido,
    })
  }

  const handleImpressedEvent = (
    { id, hospitalId, cost, currency }: SearchPageEventImpressedEventParam,
    index: number,
  ) => {
    if (user && user.id !== 0) {
      const commonEvent = createCommonEvent(navigator.userAgent, user.id)

      const eventCollector = createFetchWebEventCollector({
        baseURL: (getEnv('NEXT_PUBLIC_SOLAR_API_HOST') as string) || '',
      })

      eventCollector.emitServiceOfferViewed({
        ...commonEvent,
        Name: EventName.ServiceOfferViewed,
        Body: {
          ...commonEvent.Body,
          hospitalId,
          serviceOfferId: id,
          serviceOfferType: 'SPECIAL_OFFER',
          placementOrderInPage: treatmentTotalLength > 0 ? 2 : 1,
          itemOrderInPlacement: index + 1,
          sellingPriceAmount: cost,
          sellingPriceCurrency: currency as Currency,
          pageName: PageName.SearchTotalWeb,
        },
      })
    }
  }

  const handleClickEventMore = () => {
    postAmplitude('click_event_more', {
      ...commonAmplitudeProperties,
      treatment_ctg_id: treatmentCategoryId,
      treatment_ctg_name: treatmentCategoryName,
    })
  }
  const handleClickHospital = (hospital: HospitalInList) => {
    postAmplitude('click_hospital_in_search', {
      ...commonAmplitudeProperties,
      hospital_id: hospital?.id,
      hospital_name: hospital?.name,
      hospital_region: hospital?.sido,
    })
  }
  const handleClickPost = (post: Post) => {
    postAmplitude('click_column_in_search', {
      ...commonAmplitudeProperties,
      post_id: post?._id,
    })

    router.push({
      pathname: `/posts/${post?._id}`,
    })
  }

  useEffect(() => {
    postAmplitude('view_search_result', {
      type: category ? 'category' : 'q',
      q: q || '',
      category: category || undefined,
    })
  }, [q, category])

  useEffect(() => {
    const defaultProperties = {
      path: `${location.pathname}${location.search}`,
      referrer: document.referrer,
      eventmodule_place: getPlaceByPathname(router),
    }

    setCommonAmplitudeProperties(defaultProperties)
  }, [router, q, category])

  useEffect(() => {
    if (user && user.id !== 0) {
      sessionStorage.setItem('user_id', user.id.toString())
    } else {
      sessionStorage.removeItem('user_id')
    }
  }, [user])

  useEffect(() => {
    sendAirbridgeEvent(AirbridgeEvents.AIRBRIDGE_ECOMMERCE_SEARCH_RESULTS_VIEWED, {
      semanticAttributes: {
        query: q || category,
      },
    })
  }, [q, category])

  const hasAddCriteoBeenCalled = useRef(false)

  useEffect(() => {
    if (user?.loginId && (q || category)) {
      addCriteoEvent(user.loginId, {
        event: 'viewList',
        item:
          serviceOfferItems.length > 0
            ? serviceOfferItems.map((serviceOfferItem) => `${serviceOfferItem.serviceOffer.id}`).slice(0, 3)
            : [],
        ...(category && { category: `${category}` }),
        ...(q && { keywords: `${q}` }),
      })

      hasAddCriteoBeenCalled.current = true
    }
  }, [category, serviceOfferItems, q, user?.loginId])

  return (
    <ResponsiveLayout>
      <TopDownloadScreen userProfileImage={user?.profileImage} useGnb={false} bottomPaddingToAdd="108px">
        <SearchTemplate
          key={`${q} ${category}`}
          user={user}
          language={language}
          keyword={keyword}
          serviceOfferItems={serviceOfferItems}
          serviceOfferTotalLength={serviceOfferTotalLength}
          communityDocuments={communityDocuments}
          communityDocumentTotalLength={communityDocumentTotalLength}
          hospitals={hospitals}
          hospitalTotalLength={hospitalTotalLength}
          blogs={blogs}
          blogTotalLength={blogTotalLength}
          treatments={treatments}
          treatmentTotalLength={treatmentTotalLength}
          token={token}
          totalLength={totalLength}
          reviews={reviews}
          reviewTotalLength={reviewTotalLength}
          q={q}
          treatmentCategoryId={treatmentCategoryId}
          onClickTreatment={handleClickTreatment}
          onClickEvent={handleClickEvent}
          onImpressedEvent={handleImpressedEvent}
          onClickEventMore={handleClickEventMore}
          onClickHospital={handleClickHospital}
          onClickPost={handleClickPost}
        />
      </TopDownloadScreen>
      {serviceOfferItems?.map((serviceOfferItem) => {
        const {
          serviceOffer: {
            id,
            title,
            hospital: { name: hospitalName },
            thumbnail,
            representativeOption: {
              price: { discountedAmount, currency },
            },
          },
        } = serviceOfferItem

        try {
          const productJsonLd = getHospitalProductJsonLdWithContext({
            name: title,
            description: `${hospitalName}`,
            image: thumbnail,
            priceCurrency: currency,
            price: discountedAmount,
          })

          return <JsonLd key={id} data={productJsonLd} />
        } catch (e) {
          console.error(e)
          return <></>
        }
      })}
    </ResponsiveLayout>
  )
}

export default Search
